import {
  Anchor,
  Box,
  Button,
  Center,
  Checkbox,
  Container,
  Group,
  Loader,
  Pagination,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  MantineNotificationColor,
  MantineNotificationPosition,
} from "common/constants";
//import { DataTable } from "@mantine/datatable";
import { IconShoppingCartPlus, IconTrash } from "@tabler/icons-react";
import { completeOrder, initialize } from "api/cart.service";
import { safeApiCall } from "api/common.service";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCartStore } from "stores/useCartStore";

const ShoppingCartPage = () => {
  const { cart, removeItem, clearCart, setCart } = useCartStore();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [selectedRows, setSelectedRows] = useState<(string | number)[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(30); // default to 50
  const pagedItems = (cart?.items ?? []).slice(
    (page - 1) * pageSize,
    page * pageSize,
  );
  const customerOrder = useCartStore((state) => state.cart.customerOrder);
  const setCustomerOrder = useCartStore((state) => state.setCustomerOrder);

  const rows2 = pagedItems.map((element, index) => (
    <Table.Tr
      key={`${element.id}-${index}`}
      bg={
        selectedRows.includes(element.id)
          ? "var(--mantine-color-blue-light)"
          : undefined
      }
    >
      <Table.Td>
        <Checkbox
          aria-label="Select row"
          checked={selectedRows.includes(element.productId ?? "")}
          onChange={(event) =>
            setSelectedRows(
              event.currentTarget.checked
                ? [...selectedRows, element.productId]
                : selectedRows.filter(
                    (position) => position !== element.productId,
                  ),
            )
          }
        />
      </Table.Td>
      <Table.Td>{element.productCode}</Table.Td>
      <Table.Td>{element.title}</Table.Td>
      <Table.Td>{element.unitsCtn}</Table.Td>
      <Table.Td>${element.price.toFixed(2)}</Table.Td>

      <Table.Td>
        <Group gap="xs">
          <Button
            size="compact-xs"
            variant="light"
            disabled={busy}
            onClick={() =>
              element.quantity > 1 &&
              useCartStore
                .getState()
                .updateQuantity(element.productId, element.quantity - 1)
            }
          >
            −
          </Button>
          <Text>{element.quantity}</Text>
          <Button
            size="compact-xs"
            variant="light"
            disabled={busy}
            onClick={() =>
              useCartStore
                .getState()
                .updateQuantity(element.productId, element.quantity + 1)
            }
          >
            +
          </Button>
        </Group>
      </Table.Td>
      <Table.Td>${(element.price * element.quantity).toFixed(2)}</Table.Td>
    </Table.Tr>
  ));

  /**
   * Handles the confirmation of the order.
   * It checks if the cart is empty, shows a notification if it is,
   * and then calls the cart service to complete the order.
   * After the order is completed, it clears the cart and resets the customer order.
   * @returns  Handles the confirmation of the order.
   * It checks if the cart is empty, shows a notification if it is,
   */
  const handleConfirmOrder = async () => {
    if (cart.items.length === 0) {
      notifications.show({
        title: `Shopping Cart`,
        message: "Your cart is empty!",
        position: MantineNotificationPosition.TopCenter,
        color: MantineNotificationColor.Warn,
      });
      return;
    }

    try {
      setBusy(true);
      // confirm and complete the order
      const resp = await safeApiCall(completeOrder(cart.id, customerOrder)); // Replace with actual cart ID if applicable
      // reset the cart and customer order
      clearCart();

      // re initialize the cart
      const cartRes = await safeApiCall(initialize());
      if (cartRes) {
        setCart(cartRes.data || []);
      }

      notifications.show({
        title: `Shopping Cart`,
        message: "Your order has been submited! 🌟",
        position: MantineNotificationPosition.TopCenter,
        color: MantineNotificationColor.Success,
      });
      setCustomerOrder("");
    } catch (err: any) {
      notifications.show({
        title: `Shopping Cart`,
        message: err.message,
        position: MantineNotificationPosition.TopCenter,
        color: MantineNotificationColor.Error,
      });
    } finally {
      setTimeout(() => setBusy(false), 1500);
      navigate(0); // Redirect to the CMS page
    }
  };
  /**
   * Handles bulk deletion of selected items from the cart.
   * It iterates over the selected rows and removes each item from the cart.
   */
  const handleBulkDelete = () => {
    selectedRows.forEach((productId) => removeItem(productId));
    setSelectedRows([]);
  };
  return (
    <Container>
      <Stack gap="md">
        <Group justify="space-between">
          <Text size="lg" fw={700}>
            Your Shopping Cart ({(cart?.items ?? []).length}) items
          </Text>
          <Anchor href="/products">Continue shopping</Anchor>
        </Group>

        {busy && (
          <Center>
            <Loader variant="dots" />
          </Center>
        )}

        <Box>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>
                  <Checkbox
                    aria-label="Select all"
                    checked={pagedItems.every((item) =>
                      selectedRows.includes(item.productId),
                    )}
                    indeterminate={
                      pagedItems.some((item) =>
                        selectedRows.includes(item.productId),
                      ) &&
                      !pagedItems.every((item) =>
                        selectedRows.includes(item.productId),
                      )
                    }
                    onChange={(event) => {
                      const pageProductIds = pagedItems.map(
                        (item) => item.productId,
                      );
                      setSelectedRows(
                        event.currentTarget.checked
                          ? Array.from(
                              new Set([...selectedRows, ...pageProductIds]),
                            )
                          : selectedRows.filter(
                              (id) => !pageProductIds.includes(id.toString()),
                            ),
                      );
                    }}
                  />
                </Table.Th>
                <Table.Th>Code</Table.Th>
                <Table.Th>Title</Table.Th>
                <Table.Th>Units</Table.Th>
                <Table.Th>Carton Price</Table.Th>
                <Table.Th>Quantity</Table.Th>
                <Table.Th>Sub Total</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>{rows2}</Table.Tbody>
            <Table.Tfoot>
              <Table.Tr>
                <Table.Td
                  colSpan={6}
                  style={{ textAlign: "right", fontWeight: "bold" }}
                >
                  Total:
                </Table.Td>
                <Table.Td style={{ fontWeight: "bold" }}>
                  $
                  {(cart?.items ?? [])
                    .reduce((sum, item) => sum + item.price * item.quantity, 0)
                    .toFixed(2)}
                </Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Td colSpan={7}>
                  <Group
                    justify="space-between"
                    align="center"
                    style={{ width: "100%" }}
                  >
                    <Select
                      label="Items per page"
                      data={["10", "20", "30", "50", "100"]}
                      value={pageSize.toString()}
                      onChange={(val) => {
                        setPageSize(Number(val));
                        setPage(1);
                      }}
                      style={{ width: 150 }}
                    />

                    <Pagination
                      total={Math.ceil((cart?.items ?? []).length / pageSize)}
                      value={page}
                      onChange={setPage}
                      boundaries={1}
                      siblings={1}
                    />

                    <Button
                      onClick={handleBulkDelete}
                      disabled={selectedRows.length === 0}
                      color="red"
                      leftSection={<IconTrash size={18} />}
                    >
                      Remove Selected
                    </Button>
                  </Group>
                </Table.Td>
              </Table.Tr>
            </Table.Tfoot>
          </Table>
        </Box>

        <Group mt="md" justify="flex-end">
          <TextInput
            size="md"
            label="Customer Order #"
            ta={"right"}
            value={customerOrder}
            onChange={(e) => setCustomerOrder(e.currentTarget.value)}
            placeholder="Your Order Number"
            style={{ maxWidth: 250 }}
            styles={{ input: { textAlign: "right" } }}
          />
        </Group>
        <Group mt="md" justify="flex-end">
          <Button
            onClick={handleConfirmOrder}
            disabled={(cart?.items ?? []).length === 0}
            leftSection={<IconShoppingCartPlus size={18} />}
          >
            Confirm Order
          </Button>
        </Group>
      </Stack>
    </Container>
  );
};

export default ShoppingCartPage;
